import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isForcedLogoutError } from '../../axios/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { useBrandContext } from '../../context/BrandContext';
import { useToast } from '../../context/ToastModalContext';

import Button from '../../components/button/Button';
import SwipeToRefresh from '../../components/SwipeToRefresh/SwipeToRefresh';
import ToastHelp from '../../components/toastHelp/ToastHelp';
import CreditCardList from './creditCardList/CreditCardList';

import { getUserPaymentMethods } from '../../services/userPaymentService';
import { getUser } from '../../services/userService';

import creditCard from '../../assets/payments/credit-card.svg';
import helpIcon from '../../assets/helpIcon.svg';
import pix from '../../assets/payments/pix.svg';
import { ReactComponent as LoaderSecondary } from '../../assets/SpinnerBlack.svg';
import { ICreditCard } from '../../shared/models/creditCard/creditCard';

import './PaymentMethods.css';
import { useHeaderContext } from '../../context/HeaderContext';

const PaymentMethods: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorFetch, setErrorFetch] = useState('');
  const [cardsList, setCardsList] = useState<ICreditCard[]>();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<any>([]);
  const [showHelpToast, setShowHelpToast] = useState(false);
  const authContext = useAuth();
  const brandContext = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();
  const { showToast } = useToast();

  const loadCards = () => {
    const getPaymentMenthods = async () => {
      setIsLoading(true);
      try {
        const response = await getUserPaymentMethods();
        setCardsList(response);
        setErrorFetch('');
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        if (error?.message === 'Network Error') {
          setErrorFetch(
            'Sua conexão está instável. Por favor, tente mais tarde.'
          );
        } else {
          setErrorFetch('Falha no servidor. Por favor tente novamente.');
        }
      }
    };
    getPaymentMenthods();
    getUserProfileData();
  };

  useEffect(() => {
    return () => {};
  }, []);

  useEffect(() => {
    if (authContext.isLogged() === undefined) return;
    else if (authContext.isLogged()) loadCards();
    else navigate(`/${brandContext.brandName}`);
  }, [authContext, authContext.isLogged, brandContext.brandName, navigate]);

  useEffect(() => {
    if (errorFetch) {
      showToast({
        title: 'Falha',
        messageModal: errorFetch,
        allowButtonClosed: true,
        closeButtonText: 'Entendi',
      });
      setErrorFetch('');
    }
  }, [errorFetch, showToast]);

  const getUserProfileData = async () => {
    try {
      const response = await getUser();
      setUserInfo(response);
      setIsLoading(false);
      setErrorFetch('');
      return response;
    } catch (error: any) {
      setIsLoading(false);

      // Sessao derrubada (401/403/404): o usuario ja esta indo para o login.
      if (isForcedLogoutError(error)) return;

      showToast({
        title: 'Falha',
        messageModal:
          'Falha ao carregar informações do usuário. Por favor, tente novamente',
        textEnterButton: 'Tentar novamente',
        allowButtonClosed: true,
        handleEnterButton: () => getUserProfileData(),
        closeButtonText: 'Fechar',
      });
    }
  };

  const handleClickCreditCard = async () => {
    navigate(`/${brandContext.brandName}/addCreditCard`);
  };

  const handleClickCreditPix = async () => {
    if (userInfo?.cpf) {
      navigate(`/${brandContext.brandName}/addCredit/pix`);
    } else {
      showToast({
        title: 'CPF necessário',
        messageModal:
          'É necessário atualizar o seu CPF nas suas informações pessoais.',
        textEnterButton: 'Ir para o perfil',
        allowButtonClosed: true,
        handleEnterButton: () =>
          navigate(`/${brandContext.brandName}/userProfile`, {
            state: { cpfRequired: true },
          }),
        closeButtonText: 'Fechar',
      });
    }
  };

  useEffect(() => {
    setHeaderConfig({
      headerText: 'Adicionar crédito',
      backNavigation: () => navigate(`/${brandContext.brandName}/`),
    });
    return () => setHeaderConfig({});
  }, [brandContext.brandName, navigate, setHeaderConfig]);

  return (
    <>
      <SwipeToRefresh onSwipeDown={loadCards}>
        <div className='payment-methods-content-container'>
          <p>Escolha a forma de pagamento</p>
          {cardsList && !isLoading ? (
            <CreditCardList userInfo={userInfo} creditCardsList={cardsList} />
          ) : (
            <LoaderSecondary />
          )}
          <Button
            style={{
              marginBottom: '19px',
              color: 'var(--Neutral-Regular, #787E7D)',
              backgroundColor: '#fff',
              border: '1px solid #787E7D',
            }}
            type='button'
            children='Cartão de crédito'
            onClick={handleClickCreditCard}
            image={<img alt='add-button-icon' src={creditCard} />}
          />
          <Button
            style={{
              marginBottom: '19px',
              color: 'var(--Neutral-Regular, #787E7D)',
              backgroundColor: '#fff',
              border: '1px solid #787E7D',
            }}
            type='button'
            children='Pix'
            onClick={handleClickCreditPix}
            image={<img alt='add-button-icon' src={pix} />}
          />
        </div>
      </SwipeToRefresh>
      <div className='charger_footer_toggle-buttons'>
        <button className='help-button' onClick={() => setShowHelpToast(true)}>
          <span>{'Ajuda'}</span>
          <img alt='help-icon' src={helpIcon} />
        </button>
      </div>
      <ToastHelp
        isModalOpen={showHelpToast}
        allowButtonClosed={true}
        setShowHelpToast={setShowHelpToast}
      />
    </>
  );
};

export default PaymentMethods;
